import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import {
  ReachCampaignDetails,
  SourcingPurchaseDetails,
  SourcingService,
} from 'api-clients/monolith';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CampaignParameters } from '@/components/pages/edit-campaign/types';
import { useEditCampaignForm } from '@/components/pages/edit-campaign/use-edit-campaign-form';
import { useEditCampaignSchema } from '@/components/pages/edit-campaign/use-edit-campaign-schema';
import {
  HiringTarget,
  useHiringTarget,
} from '@/components/pages/edit-campaign/use-hiring-target';
import {
  ReachSummary,
  useReachSummary,
} from '@/components/pages/edit-campaign/use-reach-summary';
import { useHiringGoalIdParam } from '@/hooks/use-hiring-goal-id-param';
import { queryKeys } from '@/query-keys';
import { getTargetApplicants, getTargetHires } from '@/utils/targets';

type ActiveEditField =
  | 'target_applicants'
  | 'expected_cpa'
  | 'expected_conversion'
  | 'target_hires'
  | 'budget'
  | null;

interface EditCampaignContextType {
  sourcingPurchaseId: string;
  hookForm: UseFormReturn<CampaignParameters>;
  onFormSubmit: () => void;
  isSubmitting: boolean;
  campaign?: SourcingPurchaseDetails;
  selectedPublisher?: ReachCampaignDetails;
  setSelectedPublisher: (publisher?: ReachCampaignDetails) => void;
  hiringTarget: HiringTarget;
  reachSummary: ReachSummary;
  isFetching: boolean;
  closeForm: () => void;
  isFormDirty: boolean;
  isReach: boolean;
  activeEditField: ActiveEditField;
  setActiveEditField: (field: ActiveEditField) => void;
  isCompleted: boolean;
  isScheduled: boolean;
}

const EditCampaignContext = createContext<EditCampaignContextType | undefined>(
  undefined,
);

export function EditCampaignProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { sourcingPurchaseId, channelSlug } = useParams();
  const { pathname } = useLocation();
  const isReach = pathname.includes('reach');
  const navigate = useNavigate();
  const [selectedPublisher, setSelectedPublisher] = useState<
    ReachCampaignDetails | undefined
  >();
  const [activeEditField, setActiveEditField] = useState<ActiveEditField>(null);
  const { hiringGoalId: currentHiringGoalId, withHiringGoalId } =
    useHiringGoalIdParam();

  if (!sourcingPurchaseId) {
    throw new Error('Sourcing purchase ID is required');
  }

  const closeForm = useCallback(() => {
    // TODO: Update to navigate to the base channels path
    const path = pathname.split('/').slice(0, -3).join('/');

    navigate({
      pathname: path,
      search: withHiringGoalId(),
    });
  }, [pathname, withHiringGoalId, navigate]);

  // Fetch the campaign details.
  const {
    data: campaign,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.campaignDetails,
      sourcingPurchaseId,
      currentHiringGoalId,
    ],
    queryFn: () => {
      return SourcingService.getInternalApiSourcingSourcingPurchases(
        sourcingPurchaseId,
        currentHiringGoalId,
      );
    },
    enabled: !!sourcingPurchaseId,
  });

  if (isError) {
    throw new Error('Failed to fetch campaign details');
  }

  const validationSchema = useEditCampaignSchema({
    channelSlug,
    budgetSpent: campaign?.budget_spent,
    isCompleted: campaign?.status === 'completed',
  });

  const hookForm = useForm<CampaignParameters>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });
  const { setValue, formState, watch } = hookForm;

  useEffect(() => {
    if (isReach && campaign) {
      setSelectedPublisher(campaign.reach_campaigns?.[0]);
    }
  }, [campaign, setSelectedPublisher, isReach]);

  const hiringGoalSpend =
    (isReach ? selectedPublisher?.budget_spent : campaign?.budget_spent) ?? 0;
  const preHiringGoalSpend =
    (isReach
      ? selectedPublisher?.pre_hiring_goal_spend
      : campaign?.pre_hiring_goal_spend) ?? 0;
  const durationStats = isReach
    ? selectedPublisher?.duration_stats
    : campaign?.duration_stats;

  // Calculate the hiring target.
  const hiringTarget = useHiringTarget({
    hookForm,
    hiringGoalSpend,
    hiringGoal: campaign?.hiring_goal ?? undefined,
    durationStats,
    isIntegratedChannel: campaign?.sourcing_channel.is_integratable ?? false,
    preHiringGoalSpend,
  });

  // Calculate the reach summary.
  const reachSummary = useReachSummary({
    hiringTarget,
    campaign,
    selectedPublisher,
    hookForm,
  });

  // Initialize the form.
  const { onFormSubmit, isSubmitting } = useEditCampaignForm({
    sourcingPurchaseId,
    reachCampaignId: selectedPublisher?.reach_campaign_id,
    hookForm,
    isReach,
    hiringTarget,
    reachSummary,
    closeForm,
  });

  const isFormDirty = useMemo(() => {
    const isDirty =
      Object.keys(
        Object.entries(formState.dirtyFields).filter(
          ([key]) => key !== 'target_applicants' && key !== 'target_hires',
        ),
      ).length > 0;

    return isDirty;
  }, [formState]);

  const [targetCpa, targetConversion] = watch([
    'expected_cpa',
    'expected_conversion',
  ]);

  // Update target applicants and hires when hiringTarget.targetBudget changes
  useEffect(() => {
    if (!hiringTarget.targetBudget) return;

    if (activeEditField !== 'target_applicants')
      setValue(
        'target_applicants',
        getTargetApplicants(hiringTarget.targetBudget, targetCpa),
      );

    if (activeEditField !== 'target_hires')
      setValue(
        'target_hires',
        getTargetHires(hiringTarget.targetBudget, targetConversion, targetCpa),
      );
  }, [
    hiringTarget.targetBudget,
    setValue,
    activeEditField,
    targetCpa,
    targetConversion,
  ]);

  const isCompleted = useMemo(() => {
    return isReach
      ? selectedPublisher?.status === 'completed'
      : campaign?.status === 'completed';
  }, [campaign?.status, selectedPublisher?.status, isReach]);

  const isScheduled = useMemo(() => {
    return isReach
      ? selectedPublisher?.status === 'scheduled'
      : campaign?.status === 'scheduled';
  }, [campaign?.status, selectedPublisher?.status, isReach]);

  return (
    <EditCampaignContext.Provider
      value={{
        sourcingPurchaseId,
        hookForm,
        onFormSubmit,
        isSubmitting,
        campaign,
        selectedPublisher,
        setSelectedPublisher,
        hiringTarget,
        reachSummary,
        isFetching,
        closeForm,
        isFormDirty,
        isReach,
        activeEditField,
        setActiveEditField,
        isCompleted,
        isScheduled,
      }}
    >
      {children}
    </EditCampaignContext.Provider>
  );
}

export function useEditCampaignContext() {
  const context = useContext(EditCampaignContext);
  if (!context) {
    throw new Error(
      'useEditCampaignContext must be used within an EditCampaignProvider',
    );
  }
  return context;
}
